// src/rms/pages/SiteAdmin.js
// ============================================================================
// Site admin — /admin
// ============================================================================
// The owner's view. Distinct from /ad-sales, which is one department: this is
// the whole business — how many operators, on which plans, in which trades, what
// they are worth, and what the ad side is contributing.
//
// Four actor classes now exist, and none may see another's surface:
//
//   /rms         operators        rms_is_operator_member()
//   /ad-sales    ad sales staff   rms_is_ad_admin()
//   /advertiser  advertisers      rms_is_advertiser_member()
//   /admin       site admin       rms_is_platform_admin()   <- this file
//
// Site admin is a superset: it can see everything, which is exactly why it is
// separate rather than a tab bolted onto one of the others.
// ============================================================================

import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import db, { isLocalBackend } from "../data";
import { supabase } from "../config/supabaseClient";
import { computeAvails } from "../ads/avails";
import { ensureRateCard } from "../ads/rateCard";
import { VERTICAL_PRESETS } from "../verticals/presets";
import { PageHeader, Stat, StatusPill, Banner, EmptyState, Field, TextInput, Select, formatDate } from "../components/ui";
import "../styles/rms.css";
import { getLocale } from "../i18n/locale";

const AD_FREE_PRICE_CENTS = 2900;
const money = (c) => `$${((c || 0) / 100).toLocaleString(getLocale(), { maximumFractionDigits: 0 })}`;

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "operators", label: "Operators" },
  { key: "revenue", label: "Revenue" },
  { key: "storage", label: "Storage" },
  { key: "announcements", label: "Announcements" },
  { key: "access", label: "Access" },
];

// Supabase free tier. Growth problems are cheap to fix when noticed early and
// expensive when discovered at the cap, which is the whole point of this panel.
const PLAN_ALLOWANCE_BYTES = 500 * 1024 * 1024;

function SiteAdmin() {
  const { tab = "overview" } = useParams();
  const navigate = useNavigate();
  const active = TABS.some((t) => t.key === tab) ? tab : "overview";

  return (
    <div className="rms-root" style={{ minHeight: "100vh" }}>
      <header className="rms-topbar rms-topbar--global">
        <div className="rms-topbar-identity">
          <div className="rms-topbar-business">ReserveZoo — Site Admin</div>
          <div className="rms-topbar-vertical">Platform owner</div>
        </div>
        <div className="rms-topbar-meta">
          <Link className="rms-btn rms-btn--ghost rms-btn--sm" to="/ad-sales">Ad sales</Link>
          <Link className="rms-btn rms-btn--ghost rms-btn--sm" to="/rms">RMS</Link>
        </div>
      </header>

      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "24px 24px 64px" }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`rms-btn ${active === t.key ? "rms-btn--primary" : "rms-btn--ghost"} rms-btn--sm`}
              onClick={() => navigate(`/admin/${t.key}`)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {active === "overview" ? <OverviewTab /> : null}
        {active === "operators" ? <OperatorsTab /> : null}
        {active === "revenue" ? <RevenueTab /> : null}
        {active === "storage" ? <StorageTab /> : null}
        {active === "announcements" ? <AnnouncementsTab /> : null}
        {active === "access" ? <AccessTab /> : null}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------

function usePlatformData() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      await ensureRateCard();
      const [operators, assets, reservations, invoices, campaigns, advertisers, delivery] = await Promise.all([
        db.list("operators", {}).then((r) => r.data || []),
        db.list("assets", {}).then((r) => r.data || []),
        db.list("reservations", {}).then((r) => r.data || []),
        db.list("invoices", {}).then((r) => r.data || []),
        db.list("adCampaigns", {}).then((r) => r.data || []),
        db.list("advertisers", {}).then((r) => r.data || []),
        db.list("adDeliveryDaily", {}).then((r) => r.data || []),
      ]);
      const avails = await computeAvails();
      setData({ operators, assets, reservations, invoices, campaigns, advertisers, delivery, avails });
    })();
  }, []);

  return data;
}

function OverviewTab() {
  const data = usePlatformData();

  // Every hook must run on every render, so this sits ABOVE the loading return
  // and tolerates data being null. Placing it after the early return changed
  // the hook count between renders and crashed the tab.
  const byVertical = useMemo(() => {
    const operators = data?.operators || [];
    const counts = {};
    operators.forEach((o) => {
      counts[o.vertical_key] = (counts[o.vertical_key] || 0) + 1;
    });
    return VERTICAL_PRESETS.map((p) => ({
      key: p.key,
      label: p.label,
      operators: counts[p.key] || 0,
    })).sort((a, b) => b.operators - a.operators);
  }, [data]);

  if (!data) return <p style={{ color: "var(--rms-muted-strong)" }}>Loading platform data…</p>;

  const { operators, assets, reservations, campaigns, advertisers, delivery } = data;

  const adFree = operators.filter((o) => o.plan_tier === "ad_free").length;
  const free = operators.length - adFree;
  const subscriptionMrr = adFree * AD_FREE_PRICE_CENTS;
  const adRevenue = delivery.reduce((s, d) => s + (d.spend_cents || 0), 0);
  const liveCampaigns = campaigns.filter((c) => c.status === "live").length;

  return (
    <>
      <PageHeader title="Overview" subtitle="The whole business at a glance." />

      <div className="rms-stat-row">
        <Stat label="Operators" value={operators.length} hint={`${free} free · ${adFree} ad-free`} />
        <Stat label="Subscription MRR" value={money(subscriptionMrr)} hint={`${adFree} × $29`} />
        <Stat label="Ad revenue" value={money(adRevenue)} hint="delivered to date" />
        <Stat label="Advertisers" value={advertisers.length} hint={`${liveCampaigns} live campaigns`} />
      </div>

      <div className="rms-stat-row">
        <Stat label={`${assets.length} assets`} value={assets.length} hint="across all operators" />
        <Stat label="Reservations" value={reservations.length} />
        <Stat
          label="Ad-free conversion"
          value={operators.length ? `${Math.round((adFree / operators.length) * 100)}%` : "—"}
          hint="the upgrade rate that matters"
        />
        <Stat
          label="Revenue per operator"
          value={operators.length ? money(Math.round((subscriptionMrr + adRevenue) / operators.length)) : "—"}
        />
      </div>

      {operators.length < 5 ? (
        <Banner tone="info">
          These figures are from your local development data. They become meaningful once real
          accounts exist.
        </Banner>
      ) : null}

      <h2 className="rms-section-title" style={{ marginTop: 24 }}>Operators by trade</h2>
      <div className="rms-table-wrap">
        <table className="rms-table">
          <thead>
            <tr>
              <th>Vertical</th>
              <th className="rms-num">Operators</th>
              <th className="rms-num">Share</th>
              <th>Ad inventory</th>
            </tr>
          </thead>
          <tbody>
            {byVertical.map((v) => {
              const avail = data.avails.rows.find(
                (r) => r.label === v.label
              );
              return (
                <tr key={v.key}>
                  <td><strong>{v.label}</strong></td>
                  <td className="rms-num">{v.operators}</td>
                  <td className="rms-num">
                    {operators.length ? `${Math.round((v.operators / operators.length) * 100)}%` : "—"}
                  </td>
                  <td style={{ fontSize: 12.5 }}>
                    {avail
                      ? `${avail.monthlyViewable.toLocaleString(getLocale())} viewable/mo · ${avail.fillPct}% sold`
                      : <span style={{ color: "var(--rms-muted)" }}>no operators yet</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ----------------------------------------------------------------------------

function OperatorsTab() {
  const data = usePlatformData();
  const [search, setSearch] = useState("");

  if (!data) return <p style={{ color: "var(--rms-muted-strong)" }}>Loading…</p>;

  const labelOf = VERTICAL_PRESETS.reduce((acc, p) => ({ ...acc, [p.key]: p.label }), {});
  const q = search.trim().toLowerCase();
  const rows = data.operators.filter(
    (o) => !q || o.business_name.toLowerCase().includes(q) || (labelOf[o.vertical_key] || "").toLowerCase().includes(q)
  );

  const assetsFor = (id) => data.assets.filter((a) => a.operator_id === id).length;
  const reservationsFor = (id) => data.reservations.filter((r) => r.operator_id === id).length;

  return (
    <>
      <PageHeader title="Operators" subtitle={`${data.operators.length} accounts`} />

      <input
        className="rms-input"
        style={{ maxWidth: 320, marginBottom: 16 }}
        placeholder="Search operators…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {rows.length === 0 ? (
        <EmptyState title="No operators" message="Accounts appear here as businesses sign up." />
      ) : (
        <div className="rms-table-wrap">
          <table className="rms-table">
            <thead>
              <tr>
                <th>Business</th>
                <th>Trade</th>
                <th className="rms-num">Assets</th>
                <th className="rms-num">Bookings</th>
                <th>Plan</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id}>
                  <td><strong>{o.business_name}</strong></td>
                  <td style={{ fontSize: 12.5 }}>{labelOf[o.vertical_key] || o.vertical_key}</td>
                  <td className="rms-num">{assetsFor(o.id)}</td>
                  <td className="rms-num">{reservationsFor(o.id)}</td>
                  <td>
                    <StatusPill
                      status={o.plan_tier === "ad_free" ? "active" : "pending"}
                      label={o.plan_tier === "ad_free" ? "Ad-Free" : "Free"}
                    />
                  </td>
                  <td style={{ fontSize: 12.5 }}>{formatDate(o.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ----------------------------------------------------------------------------

function RevenueTab() {
  const data = usePlatformData();
  if (!data) return <p style={{ color: "var(--rms-muted-strong)" }}>Loading…</p>;

  const adFree = data.operators.filter((o) => o.plan_tier === "ad_free").length;
  const subscriptionMrr = adFree * AD_FREE_PRICE_CENTS;
  const adRevenue = data.delivery.reduce((s, d) => s + (d.spend_cents || 0), 0);
  const unsoldValue = data.avails.rows.reduce((s, r) => s + r.unsoldValueCents, 0);

  return (
    <>
      <PageHeader
        title="Revenue"
        subtitle="Two lines: subscriptions from operators who want the ads gone, and advertisers who want to reach the ones who don't."
      />

      <div className="rms-stat-row">
        <Stat label="Subscription MRR" value={money(subscriptionMrr)} hint={`${adFree} ad-free accounts`} />
        <Stat label="Ad revenue delivered" value={money(adRevenue)} />
        <Stat label="Unsold inventory" value={money(unsoldValue)} hint="per month, at rack" />
        <Stat label="Combined MRR" value={money(subscriptionMrr + adRevenue)} />
      </div>

      <Banner tone="info">
        <div>
          The two lines pull against each other by design. Every operator who upgrades removes
          inventory but adds ${AD_FREE_PRICE_CENTS / 100}/mo of predictable revenue — and an
          ad-supported operator is only worth more than that if their vertical's inventory is
          actually sold. That is what the unsold figure above is telling you.
        </div>
      </Banner>

      <h2 className="rms-section-title" style={{ marginTop: 20 }}>Where the ad money is</h2>
      <div className="rms-table-wrap">
        <table className="rms-table">
          <thead>
            <tr>
              <th>Vertical</th>
              <th className="rms-num">Operators</th>
              <th className="rms-num">Viewable/mo</th>
              <th className="rms-num">Fill</th>
              <th className="rms-num">Unsold value</th>
            </tr>
          </thead>
          <tbody>
            {data.avails.rows.map((r) => (
              <tr key={r.adCategory}>
                <td><strong>{r.label}</strong></td>
                <td className="rms-num">{r.operators}</td>
                <td className="rms-num">{r.monthlyViewable.toLocaleString(getLocale())}</td>
                <td className="rms-num">{r.fillPct}%</td>
                <td className="rms-num"><strong>{money(r.unsoldValueCents)}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ----------------------------------------------------------------------------

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  const units = ["kB", "MB", "GB", "TB"];
  let value = n / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
}

/** Whole days since an ISO timestamp, or null. */
function daysSince(value) {
  if (!value) return null;
  return Math.floor((Date.now() - new Date(value).getTime()) / 86400000);
}

/**
 * Scheduled work, and whether it is actually happening.
 *
 * A cron job you cannot see the last run of is indistinguishable from one that
 * stopped two months ago, which is the failure mode that matters here: the free
 * tier pauses after 7 days of inactivity, and by the time anyone notices, the
 * thing that was supposed to prevent it has been dead for weeks.
 */
function SchedulePanel({ jobs, heartbeat }) {
  const beatAge = daysSince(heartbeat?.last_beat_at);
  // The pause threshold is 7 days; 3 is early enough to still act.
  const beatStale = beatAge === null || beatAge >= 3;

  return (
    <section className="rms-card rms-card-pad" style={{ marginTop: 16 }}>
      <h2 className="rms-section-title">Scheduled work</h2>

      {!jobs.length ? (
        <Banner tone="warn">
          No <code>rms_*</code> pg_cron jobs are visible. Either the extension isn't enabled
          (Database → Extensions → <code>pg_cron</code>) or the scheduling block in
          <code> 20260817090000_rms_scheduled_maintenance.sql</code> was skipped — it warns rather
          than fails, so a migration can apply cleanly and still leave nothing scheduled.
        </Banner>
      ) : (
        <div className="rms-table-wrap">
          <table className="rms-table" style={{ minWidth: 0 }}>
            <thead>
              <tr>
                <th>Job</th>
                <th>Schedule (UTC)</th>
                <th>Last run</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.job_name}>
                  <td style={{ fontSize: 12.5 }}>
                    <strong>{j.job_name.replace("rms_", "")}</strong>
                    {j.active ? null : <StatusPill status="cancelled" label="paused" />}
                  </td>
                  <td style={{ fontSize: 12.5 }}><code>{j.schedule}</code></td>
                  <td style={{ fontSize: 12.5 }}>
                    {j.last_run_at ? formatDate(j.last_run_at) : <span style={{ color: "var(--rms-warn)" }}>never</span>}
                  </td>
                  <td style={{ fontSize: 12.5 }}>
                    {j.last_status ? (
                      <StatusPill
                        status={j.last_status === "succeeded" ? "completed" : "cancelled"}
                        label={j.last_status}
                      />
                    ) : "—"}
                    {j.last_status && j.last_status !== "succeeded" && j.last_message ? (
                      <div style={{ color: "var(--rms-danger)", marginTop: 4 }}>{j.last_message}</div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 style={{ fontSize: 13, margin: "18px 0 6px" }}>Heartbeat</h3>
      {heartbeat ? (
        <p style={{ fontSize: 13.5, color: "var(--rms-muted-strong)", margin: 0 }}>
          Last beat {formatDate(heartbeat.last_beat_at)}
          {beatAge !== null ? ` — ${beatAge} day${beatAge === 1 ? "" : "s"} ago` : ""} ·{" "}
          {Number(heartbeat.beats).toLocaleString(getLocale())} total · via <code>{heartbeat.source}</code>
        </p>
      ) : (
        <p style={{ fontSize: 13.5, color: "var(--rms-muted)", margin: 0 }}>No heartbeat recorded.</p>
      )}

      {beatStale ? (
        <Banner tone="warn">
          The in-database heartbeat is stale. Note that it is only ever a secondary signal: Supabase
          measures inactivity at the API layer, and pg_cron never touches PostgREST. The check that
          actually keeps the project awake is the daily GitHub Action in
          <code> .github/workflows/keepalive.yml</code> — confirm that is still green before
          trusting anything here.
        </Banner>
      ) : null}
    </section>
  );
}

/**
 * Storage growth.
 *
 * The concern this exists to answer is not "how big is the database" but "is it
 * growing with the business or with background noise?" Ad telemetry outruns
 * real business data by roughly forty to one — around 1,300 impression rows per
 * operator per month against perhaps 30 bookings — so the interesting number is
 * always the ratio between the top row and the rest.
 */
function StorageTab() {
  const [state, setState] = useState({
    status: "loading",
    rows: [],
    totalBytes: 0,
    jobs: [],
    heartbeat: null,
    error: null,
  });
  const [maintenance, setMaintenance] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = React.useCallback(async () => {
    if (isLocalBackend) {
      setState({ status: "local", rows: [], totalBytes: 0, error: null });
      return;
    }
    setState((s) => ({ ...s, status: "loading" }));

    const [stats, total, jobs, beat] = await Promise.all([
      supabase.rpc("rms_storage_stats"),
      supabase.rpc("rms_storage_total_bytes"),
      supabase.rpc("rms_maintenance_status"),
      supabase.rpc("rms_heartbeat_status"),
    ]);

    if (stats.error) {
      setState({ status: "error", rows: [], totalBytes: 0, error: stats.error.message });
      return;
    }
    setState({
      status: "ready",
      rows: stats.data || [],
      totalBytes: Number(total.data) || 0,
      jobs: jobs.data || [],
      heartbeat: beat.data?.[0] || null,
      error: null,
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runMaintenance = async () => {
    setBusy(true);
    setMaintenance(null);
    const { data, error } = await supabase.rpc("rms_maintain_storage");
    setBusy(false);
    if (error) {
      setMaintenance({ error: error.message });
      return;
    }
    setMaintenance({ actions: data || [] });
    await load();
  };

  if (state.status === "local") {
    return (
      <>
        <PageHeader title="Storage" subtitle="Where the database is growing." />
        <Banner tone="info">
          Running on the local backend, so there is no Postgres to measure — everything lives in this
          browser's storage. Switch <code>VITE_RMS_BACKEND</code> to <code>supabase</code> to see real
          table sizes.
        </Banner>
      </>
    );
  }

  if (state.status === "error") {
    return (
      <>
        <PageHeader title="Storage" subtitle="Where the database is growing." />
        <Banner tone="warn">
          Could not read storage statistics: {state.error}. These functions are restricted to
          <code> rms_is_platform_admin()</code>, so this is what a non-owner would see.
        </Banner>
      </>
    );
  }

  if (state.status === "loading") {
    return (
      <>
        <PageHeader title="Storage" subtitle="Where the database is growing." />
        <p style={{ fontSize: 13.5, color: "var(--rms-muted)" }}>Measuring…</p>
      </>
    );
  }

  const usedPct = Math.min(100, (state.totalBytes / PLAN_ALLOWANCE_BYTES) * 100);
  const rmsBytes = state.rows.reduce((sum, r) => sum + Number(r.total_bytes || 0), 0);
  const biggest = state.rows[0];

  return (
    <>
      <PageHeader
        title="Storage"
        subtitle="Where the database is growing."
        actions={
          <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={runMaintenance} disabled={busy}>
            {busy ? "Running…" : "Run maintenance now"}
          </button>
        }
      />

      <div className="rms-stat-row">
        <Stat label="Database" value={formatBytes(state.totalBytes)} hint={`${usedPct.toFixed(1)}% of ${formatBytes(PLAN_ALLOWANCE_BYTES)}`} />
        <Stat label="ReserveZoo tables" value={formatBytes(rmsBytes)} hint="everything else is Postgres itself" />
        <Stat label="Tables" value={state.rows.length} />
        <Stat label="Largest" value={biggest ? biggest.table_name.replace("rms_", "") : "—"} hint={biggest ? formatBytes(biggest.total_bytes) : undefined} />
      </div>

      {usedPct > 70 ? (
        <Banner tone="warn">
          Past 70% of the free-tier allowance. Check that the top rows below are business data —
          bookings, invoices, customers — and not ad telemetry that retention should have expired.
        </Banner>
      ) : null}

      {maintenance?.error ? (
        <Banner tone="warn">Maintenance failed: {maintenance.error}</Banner>
      ) : null}

      {maintenance?.actions ? (
        <Banner tone="ok">
          <div>
            <strong>Maintenance complete.</strong>
            <ul style={{ margin: "6px 0 0", paddingInlineStart: 18, fontSize: 13 }}>
              {maintenance.actions.length ? (
                maintenance.actions.map((a, i) => (
                  <li key={`${a.action}-${i}`}>
                    <code>{a.action}</code> — {a.detail}
                  </li>
                ))
              ) : (
                <li>Nothing needed doing.</li>
              )}
            </ul>
          </div>
        </Banner>
      ) : null}

      <div className="rms-table-wrap" style={{ marginTop: 16 }}>
        <table className="rms-table">
          <thead>
            <tr>
              <th>Table</th>
              <th className="rms-num">Rows (est.)</th>
              <th className="rms-num">Data</th>
              <th className="rms-num">Indexes</th>
              <th className="rms-num">Total</th>
              <th className="rms-num">Partitions</th>
            </tr>
          </thead>
          <tbody>
            {state.rows.map((r) => (
              <tr key={r.table_name}>
                <td style={{ fontSize: 12.5 }}>{r.table_name}</td>
                <td className="rms-num">{Number(r.row_estimate).toLocaleString(getLocale())}</td>
                <td className="rms-num">{formatBytes(r.table_bytes)}</td>
                {/* Indexes larger than the data they index is normal while
                    empty and worth a second look once there is traffic. */}
                <td className="rms-num" style={{ color: Number(r.index_bytes) > Number(r.table_bytes) ? "var(--rms-warn)" : undefined }}>
                  {formatBytes(r.index_bytes)}
                </td>
                <td className="rms-num"><strong>{formatBytes(r.total_bytes)}</strong></td>
                <td className="rms-num">{r.partitions || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SchedulePanel jobs={state.jobs} heartbeat={state.heartbeat} />

      <section className="rms-card rms-card-pad" style={{ marginTop: 16 }}>
        <h2 className="rms-section-title">What maintenance does</h2>
        <ul style={{ fontSize: 13.5, color: "var(--rms-muted-strong)", lineHeight: 1.6, paddingInlineStart: 18, margin: 0 }}>
          <li>Extends monthly partitions three months ahead, so writes never hit a missing partition.</li>
          <li>Rolls gate events up into <code>rms_access_event_monthly</code> before they expire — the count survives even when the individual rows do not.</li>
          <li>
            Drops ad impression and click partitions older than 90 days, and access event partitions
            older than 12 months. Dropping a partition is instant; deleting the rows would bloat the
            table until a <code>VACUUM FULL</code> took an exclusive lock on it.
          </li>
          <li>
            <strong>Refuses</strong> to drop an impression partition that still holds rows with no
            matching <code>rms_ad_delivery_daily</code> entry. Losing raw impressions is fine; losing
            what an advertiser owes is not.
          </li>
        </ul>
      </section>
    </>
  );
}

// ----------------------------------------------------------------------------

/**
 * Notices to rental businesses.
 *
 * Drafts are invisible until published, and publishing is a separate,
 * deliberate action — writing an outage notice and having it appear on 400
 * dashboards mid-sentence is not a thing that should be possible.
 */
function AnnouncementsTab() {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  const load = React.useCallback(async () => {
    const result = await db.list("announcements", {}, { orderBy: "created_at", direction: "desc" });
    if (!result.success) return setProblem(result.error);
    setRows(result.data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (publish) => {
    setBusy(true);
    setProblem(null);

    const payload = {
      title: draft.title,
      body: draft.body,
      severity: draft.severity,
      audience_vertical: draft.audience_vertical || null,
      audience_plan_tier: draft.audience_plan_tier || null,
      send_email: !!draft.send_email,
      published_at: publish ? new Date().toISOString() : draft.published_at || null,
      expires_at: draft.expires_at || null,
    };

    const result = draft.id
      ? await db.update("announcements", draft.id, payload)
      : await db.create("announcements", payload);

    setBusy(false);
    if (!result.success) return setProblem(result.error);
    setDraft(null);
    load();
  };

  return (
    <>
      <PageHeader
        title="Announcements"
        subtitle="Notices to rental businesses — outages, changes, news."
        actions={
          <button type="button" className="rms-btn rms-btn--primary" onClick={() => setDraft(blankAnnouncement())}>
            Write one
          </button>
        }
      />

      {problem ? <Banner tone="warn">{problem}</Banner> : null}

      {draft ? (
        <section className="rms-card rms-card-pad" style={{ marginBottom: 16 }}>
          <h2 className="rms-section-title">{draft.id ? "Edit notice" : "New notice"}</h2>

          <div className="rms-field-grid">
            <Field label="Title" required>
              <TextInput value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} placeholder="Scheduled maintenance on Sunday" />
            </Field>
            <Field label="Severity">
              <Select
                value={draft.severity}
                onChange={(v) => setDraft({ ...draft, severity: v })}
                options={[
                  { value: "info", label: "Info — news, features" },
                  { value: "warning", label: "Warning — something needs attention" },
                  { value: "critical", label: "Critical — outage, urgent" },
                ]}
              />
            </Field>
            <Field label="Only this trade" help="Leave blank to send to everyone.">
              <Select
                value={draft.audience_vertical || ""}
                onChange={(v) => setDraft({ ...draft, audience_vertical: v })}
                options={VERTICAL_PRESETS.map((p) => ({ value: p.key, label: p.label }))}
              />
            </Field>
            <Field label="Only this plan" help="Leave blank for both.">
              <Select
                value={draft.audience_plan_tier || ""}
                onChange={(v) => setDraft({ ...draft, audience_plan_tier: v })}
                options={[
                  { value: "free", label: "Free" },
                  { value: "ad_free", label: "Ad-Free" },
                ]}
              />
            </Field>
            <Field label="Stop showing on" help="Leave blank to show until you remove it.">
              <input
                className="rms-input"
                type="date"
                value={draft.expires_at ? draft.expires_at.slice(0, 10) : ""}
                onChange={(e) => setDraft({ ...draft, expires_at: e.target.value ? `${e.target.value}T00:00:00.000Z` : null })}
              />
            </Field>
          </div>

          <textarea
            className="rms-input"
            rows={5}
            style={{ resize: "vertical", marginTop: 8 }}
            placeholder="What's happening, what it means for them, and what they need to do."
            value={draft.body}
            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
          />

          <label className="rms-checkline">
            <input
              type="checkbox"
              checked={!!draft.send_email}
              onChange={(e) => setDraft({ ...draft, send_email: e.target.checked })}
            />
            Also email it
          </label>
          <p className="rms-help" style={{ margin: "0 0 12px 24px" }}>
            Notices always appear in the app. Email as well only when it genuinely cannot wait until
            someone next signs in — that is the difference between a useful notice and one people
            filter.
          </p>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="rms-btn rms-btn--ghost" onClick={() => save(false)} disabled={busy || !draft.title.trim()}>
              Save as draft
            </button>
            <button type="button" className="rms-btn rms-btn--primary" onClick={() => save(true)} disabled={busy || !draft.title.trim() || !draft.body.trim()}>
              Publish now
            </button>
            <button type="button" className="rms-btn rms-btn--ghost" onClick={() => setDraft(null)}>
              Cancel
            </button>
          </div>
        </section>
      ) : null}

      {!rows.length ? (
        <EmptyState title="Nothing written yet" message="Notices appear on every operator's dashboard." />
      ) : (
        <div className="rms-table-wrap">
          <table className="rms-table">
            <thead>
              <tr>
                <th>Notice</th>
                <th>Audience</th>
                <th>Status</th>
                <th>Published</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{row.title}</strong>
                    <div style={{ fontSize: 12, color: "var(--rms-muted)" }}>
                      {row.severity}{row.send_email ? " · emailed" : ""}
                    </div>
                  </td>
                  <td style={{ fontSize: 12.5 }}>
                    {row.audience_vertical || row.audience_plan_tier
                      ? [row.audience_vertical, row.audience_plan_tier].filter(Boolean).join(" · ")
                      : "Everyone"}
                  </td>
                  <td>
                    <StatusPill
                      status={row.published_at ? "active" : "pending"}
                      label={row.published_at ? "published" : "draft"}
                    />
                  </td>
                  <td style={{ fontSize: 12.5 }}>{row.published_at ? formatDate(row.published_at) : "—"}</td>
                  <td className="rms-num">
                    <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => setDraft(row)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function blankAnnouncement() {
  return {
    id: null, title: "", body: "", severity: "info",
    audience_vertical: "", audience_plan_tier: "",
    send_email: false, published_at: null, expires_at: null,
  };
}

// ----------------------------------------------------------------------------

function AccessTab() {
  return (
    <>
      <PageHeader title="Access" subtitle="Who can reach which surface." />

      <div className="rms-table-wrap">
        <table className="rms-table">
          <thead>
            <tr>
              <th>Surface</th>
              <th>Who</th>
              <th>Enforced by</th>
              <th>Sees</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>/rms</strong></td>
              <td>Rental operators</td>
              <td><code>rms_is_operator_member()</code></td>
              <td>Only their own business data</td>
            </tr>
            <tr>
              <td><strong>/ad-sales</strong></td>
              <td>Ad sales staff</td>
              <td><code>rms_is_ad_admin()</code></td>
              <td>All advertisers, campaigns, avails, rate card incl. floors</td>
            </tr>
            <tr>
              <td><strong>/advertiser</strong></td>
              <td>Advertisers</td>
              <td><code>rms_is_advertiser_member()</code></td>
              <td>Only their own campaigns and delivery. Never the rate card floor.</td>
            </tr>
            <tr>
              <td><strong>/admin</strong></td>
              <td>You</td>
              <td><code>rms_is_platform_admin()</code></td>
              <td>Everything</td>
            </tr>
          </tbody>
        </table>
      </div>

      <Banner tone="warn">
        <div>
          <strong>Before going live:</strong> these routes are open on the local backend because
          there is no authentication to check. With Supabase attached, each is gated by the function
          above — and both <code>rms_ad_admins</code> and <code>rms_platform_admins</code> start
          empty, so you must insert your own user id into them or you will lock yourself out.
        </div>
      </Banner>
    </>
  );
}

export default SiteAdmin;
